<script setup lang="ts">
/**
 * Customer-facing orders ("Reservations" in legacy state name). Renders the
 * surveys list scoped to a project — legacy hard-coded `project_id = 1`, which
 * is the EMSL system project that holds all customer orders. Preserves that
 * convention so the existing data shows up; revisit when the model grows up.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import moment from 'moment';
import { saveAs } from 'file-saver';
import Paginator from 'primevue/paginator';
import SplitButton from 'primevue/splitbutton';
import type { MenuItem } from 'primevue/menuitem';
import tilePattern from '@/assets/images/pattern-project.svg';
import { surveysApi, type Survey } from '@/services/api/surveys';
import { exportsApi } from '@/services/api/exports';
import { usePagination } from '@/composables/usePagination';
import { useGridList } from '@/composables/useGridList';
import { useNotifier } from '@/composables/notifier';
import { useAppDialog } from '@/composables/useAppDialog';
import { useDateRangePresets } from '@/composables/useDateRangePresets';
import DateRangeDropdown from '@/components/DateRangeDropdown.vue';
import OrderDetailsDialog from '@/views/emsl/OrderDetailsDialog.vue';
import LabResultsDialog from '@/views/emsl/LabResultsDialog.vue';

const ORDERS_PROJECT_ID = 1;
// Mirrors SURVEYS_MAX_LOOKBACK_DAYS on the server.
const MAX_LOOKBACK_DAYS = 730;

const {
  preset: datePreset,
  dateRange,
  options: presetOptions,
  minPickerDate,
  maxPickerDate,
} = useDateRangePresets({ defaultPreset: '1y', maxLookbackDays: MAX_LOOKBACK_DAYS });

const route = useRoute();
const paging = usePagination(25);
const grid = useGridList();
const dialog = useAppDialog();
const surveys = ref<Survey[]>([]);
const search = ref('');
const loading = ref(false);
const notify = useNotifier();

async function reload(): Promise<void> {
  loading.value = true;
  try {
    const [since, until] = dateRange.value;
    const range = {
      ...(since ? { since: since.toISOString() } : {}),
      ...(until ? { until: until.toISOString() } : {}),
    };
    const result = search.value
      ? await surveysApi.searchPaged(
          ORDERS_PROJECT_ID,
          search.value,
          paging.page.value,
          paging.perPage.value,
          range,
        )
      : await surveysApi.indexPaged(
          ORDERS_PROJECT_ID,
          paging.page.value,
          paging.perPage.value,
          range,
        );
    surveys.value = result.data;
    paging.setCount(result.pagingInfo.count);
    if (result.clampedToDays !== undefined) {
      notify.warn(
        `Date range narrowed to the last ${result.clampedToDays} days — the server caps how far back you can query.`,
        'Date range clamped',
      );
    }
  } finally {
    loading.value = false;
  }
}

async function runSearch(): Promise<void> {
  paging.setPage(1);
  await reload();
}

async function clearSearch(): Promise<void> {
  if (search.value.length === 0) return;
  search.value = '';
  await runSearch();
}

async function onPaginate(event: { page: number; rows: number }): Promise<void> {
  paging.perPage.value = event.rows;
  paging.setPage(event.page + 1);
  await reload();
}

// Date range immediately re-queries when both ends are set.
watch(
  dateRange,
  ([s, u]) => {
    if (s && u) void runSearch();
  },
  { deep: true },
);

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = moment(iso);
  return m.isValid() ? m.format('LL') : '—';
}

function isReservation(survey: Survey): boolean {
  return (survey.template_name ?? '').includes('Reservation');
}

/** Whether the "Order PDF" action should appear. Mirrors the admin Lab Orders
 *  view (showCOC): the Order PDF is produced by Laboratory::Coc, which returns
 *  nil for LOMS/paper-origin orders — so hide it for 'Paper COC' (as well as
 *  reservations and guid-less manual orders, per the admin logic). */
function showOrderPdf(survey: Survey): boolean {
  if (!survey.guid) return false;
  if (survey.template_name === 'Lab Reservation') return false;
  if (survey.template_name === 'Paper COC') return false;
  return true;
}

async function viewOrder(survey: Survey): Promise<void> {
  try {
    const data = await surveysApi.raw(ORDERS_PROJECT_ID, survey.id);
    dialog.open(OrderDetailsDialog, {
      header: `Order ${survey.reservation_code ?? `#${survey.id}`}`,
      width: '64rem',
      data,
    });
  } catch {
    notify.error('Could not load order data.');
  }
}

async function viewLabResults(survey: Survey): Promise<void> {
  try {
    const data = await surveysApi.labResults(ORDERS_PROJECT_ID, survey.id);
    dialog.open(LabResultsDialog, {
      header: `Lab Results — ${survey.reservation_code ?? `#${survey.id}`}`,
      width: '64rem',
      data,
    });
  } catch {
    notify.error('Could not load lab results.');
  }
}

async function downloadOrderXls(survey: Survey): Promise<void> {
  if (!survey.guid) {
    notify.error('No GUID on this survey — XLS not available.');
    return;
  }
  try {
    const bytes = await exportsApi.xls(survey.guid);
    const blob = new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `${survey.reservation_code ?? survey.id}.xlsx`);
  } catch {
    notify.error('Could not download XLS.');
  }
}

async function downloadPdf(survey: Survey): Promise<void> {
  if (!survey.guid) {
    notify.error('No GUID on this survey — PDF not available.');
    return;
  }
  try {
    const bytes = await exportsApi.pdf(survey.guid);
    const blob = new Blob([bytes], { type: 'application/pdf' });
    saveAs(blob, `${survey.reservation_code ?? survey.id}.pdf`);
  } catch {
    notify.error('Could not download PDF.');
  }
}

/** Build the dropdown items per-row so optional actions can be conditional. */
function menuItems(survey: Survey): MenuItem[] {
  const items: MenuItem[] = [
    { label: 'View Order', icon: 'pi pi-eye', command: () => void viewOrder(survey) },
  ];
  if (survey.lab_results_id) {
    items.push({
      label: 'View Lab Results',
      icon: 'pi pi-list',
      command: () => void viewLabResults(survey),
    });
  }
  // XLS / PDF only work when the survey has a GUID (the controllers look it up
  // via Survey.where(guid: …), and the route is unauthenticated for that exact
  // reason — the GUID is the auth token).
  if (survey.guid) {
    items.push({ separator: true });
    items.push({
      label: 'Order XLS',
      icon: 'pi pi-file-excel',
      command: () => void downloadOrderXls(survey),
    });
    if (showOrderPdf(survey)) {
      items.push({
        label: 'Order PDF',
        icon: 'pi pi-file-pdf',
        command: () => void downloadPdf(survey),
      });
    }
  }
  if (survey.watermarked_at && survey.photo_download_link) {
    items.push({
      label: 'Download Photos',
      icon: 'pi pi-image',
      url: survey.photo_download_link,
      target: '_blank',
    });
  }
  if (survey.report_kit_zip) {
    items.push({ separator: true });
    items.push({
      label: 'Download Report Kit',
      icon: 'pi pi-download',
      url: survey.report_kit_zip,
      target: '_blank',
    });
  }
  return items;
}

const empty = computed(() => !loading.value && surveys.value.length === 0);

onMounted(async () => {
  // Optional remote-link entry: `/orders?reservationCode=…` deep-links into a
  // pre-filtered list. Legacy UI-Router carried this as a state param without
  // declaring it in the URL; Vue Router 4 needs an explicit query param.
  const raw = route.query.reservationCode;
  const code = Array.isArray(raw) ? raw[0] : raw;
  if (typeof code === 'string' && code.length > 0) {
    search.value = code;
  }
  await reload();
});
</script>

<template>
  <section class="orders">
    <header class="orders__header">
      <h1>Orders</h1>
      <div class="orders__view-toggle" role="group" aria-label="View type">
        <button
          type="button"
          :aria-pressed="grid.viewType.value === 'list'"
          :class="{ 'is-active': grid.viewType.value === 'list' }"
          title="List view"
          @click="grid.switchViewType('list')"
        >
          ☰ List
        </button>
        <button
          type="button"
          :aria-pressed="grid.viewType.value === 'grid'"
          :class="{ 'is-active': grid.viewType.value === 'grid' }"
          title="Grid view"
          @click="grid.switchViewType('grid')"
        >
          ▦ Grid
        </button>
      </div>
    </header>

    <form class="orders__controls" @submit.prevent="runSearch">
      <div class="orders__search-field">
        <input
          v-model="search"
          type="search"
          placeholder="Type here to search orders…"
        />
        <button
          v-if="search.length > 0"
          type="button"
          class="orders__clear"
          aria-label="Clear search"
          title="Clear search"
          @click="clearSearch"
        >
          ×
        </button>
      </div>

      <DateRangeDropdown
        v-model:preset="datePreset"
        v-model:date-range="dateRange"
        :options="presetOptions"
        :min-date="minPickerDate"
        :max-date="maxPickerDate"
        class="orders__date"
      />

      <button type="submit">Search</button>
    </form>

    <p v-if="loading" class="orders__state">Loading…</p>
    <p v-else-if="empty" class="orders__state"><i>(No orders found.)</i></p>

    <!-- ── LIST VIEW ──────────────────────────────────────────────────── -->
    <table
      v-else-if="grid.viewType.value === 'list'"
      class="orders__table"
      summary="Completed Inspections"
    >
      <thead>
        <tr>
          <th>Reservation Code</th>
          <th>EMSL Order ID</th>
          <th>Project Name</th>
          <th>Address</th>
          <th>Submitted By</th>
          <th>Submitted Date</th>
          <th class="orders__actions-th" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="s in surveys" :key="s.id">
          <td>{{ s.reservation_code ?? '—' }}</td>
          <td class="orders__order-id">{{ s.emsl_order_id ?? '—' }}</td>
          <td>{{ s.project_name ?? '—' }}</td>
          <td>{{ s.address1 ?? '—' }}</td>
          <td>{{ s.submitted_by ?? s.er_inspector_name ?? '—' }}</td>
          <td>{{ formatDate(s.created_at) }}</td>
          <td class="orders__actions">
            <SplitButton
              v-if="!isReservation(s)"
              label="View"
              size="small"
              :model="menuItems(s)"
              @click="viewOrder(s)"
            />
            <span v-else class="orders__reservation-tag">Reservation</span>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- ── GRID VIEW ─────────────────────────────────────────────────── -->
    <ul v-else class="orders__tiles">
      <li
        v-for="s in surveys"
        :key="s.id"
        class="orders__tile"
        :style="{
          backgroundImage:
            `linear-gradient(rgba(17, 24, 39, 0.78), rgba(17, 24, 39, 0.78)), url(${tilePattern})`,
        }"
      >
        <header class="orders__tile-header">
          <h3>{{ s.reservation_code ?? `#${s.id}` }}</h3>
          <p v-if="s.emsl_order_id" class="orders__tile-order-id">{{ s.emsl_order_id }}</p>
          <p class="orders__tile-date">{{ formatDate(s.created_at) }}</p>
        </header>
        <p v-if="s.project_name || s.address1" class="orders__tile-project">
          <span v-if="s.project_name">{{ s.project_name }}</span>
          <span v-if="s.address1" class="orders__tile-address">{{ s.address1 }}</span>
        </p>
        <p class="orders__tile-submitted">
          By {{ s.submitted_by ?? s.er_inspector_name ?? '—' }}
        </p>
        <footer class="orders__tile-actions">
          <SplitButton
            v-if="!isReservation(s)"
            label="View"
            size="small"
            :model="menuItems(s)"
            @click="viewOrder(s)"
          />
          <span v-else class="orders__reservation-tag">Reservation</span>
        </footer>
      </li>
    </ul>

    <Paginator
      v-if="paging.count.value > 0"
      class="orders__pager"
      :rows="paging.perPage.value"
      :total-records="paging.count.value"
      :first="(paging.page.value - 1) * paging.perPage.value"
      :rows-per-page-options="[10, 25, 50, 100]"
      template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      current-page-report-template="Showing {first} – {last} of {totalRecords}"
      @page="onPaginate"
    />
  </section>
</template>

<style scoped>
.orders {
}
.orders__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}
.orders__view-toggle {
  display: inline-flex;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  overflow: hidden;
}
.orders__view-toggle button {
  padding: 0.375rem 0.875rem;
  background: #fff;
  color: #4b5563;
  border: 0;
  border-right: 1px solid #d1d5db;
  font-size: 0.875rem;
  cursor: pointer;
}
.orders__view-toggle button:last-child {
  border-right: 0;
}
.orders__view-toggle button.is-active {
  background: #2563eb;
  color: #fff;
}

.orders__controls {
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
  flex-wrap: wrap;
  /* Keep the search/filter bar in view while the results scroll beneath it —
   * same treatment as the admin Lab Orders page. --app-header-offset (set on
   * the app root) is the sticky masthead's height in top layout, and 0 in the
   * side-ribbon layout. The negative inline margins bleed the bar's background
   * out to the .app__main padding edges (1.5rem) so scrolling grid-tile shadows
   * don't peek past its sides. */
  position: sticky;
  top: var(--app-header-offset, 0px);
  z-index: 20;
  background: #fafbfc;
  margin: 0 -1.5rem 1.5rem;
  padding: 0.75rem 1.5rem;
}
.orders__search-field {
  position: relative;
  flex: 1 1 22rem;
  display: flex;
  min-width: 16rem;
}
.orders__search-field input {
  flex: 1;
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}
.orders__date {
  flex-shrink: 0;
}
.orders__clear {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: #e5e7eb;
  color: #4b5563;
  border: 0;
  border-radius: 50%;
  font-size: 0.875rem;
  line-height: 1;
  cursor: pointer;
}
.orders__clear:hover {
  background: #d1d5db;
  color: #1f2937;
}
.orders__controls > button[type='submit'] {
  padding: 0.5rem 1rem;
  background: #2563eb;
  color: #fff;
  border: 0;
  border-radius: 0.375rem;
  cursor: pointer;
}
.orders__state {
  color: #6b7280;
}
/* List view */
.orders__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
}
.orders__table th,
.orders__table td {
  padding: 0.625rem 0.75rem;
  border-bottom: 1px solid #f3f4f6;
  text-align: left;
  vertical-align: middle;
}
.orders__table thead th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}
.orders__table tbody tr:hover {
  background: #fafbff;
}
.orders__order-id {
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 0.8125rem;
}
.orders__actions-th {
  width: 10rem;
}
.orders__actions {
  white-space: nowrap;
  display: flex;
  gap: 0.25rem;
  align-items: center;
  justify-content: flex-end;
}
.orders__reservation-tag {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
}

/* Grid view — same patterned-tile treatment as Lab Orders. */
.orders__tiles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(18rem, 1fr));
  gap: 1rem;
  list-style: none;
  padding: 0;
  margin: 0;
}
.orders__tile {
  display: flex;
  flex-direction: column;
  background-color: transparent;
  background-position: 0 0;
  background-repeat: repeat;
  border-radius: 0.5rem;
  box-shadow: 2px 3px 10px 2px rgba(31, 41, 51, 0.34);
  padding: 0;
  overflow: hidden;
  min-height: 12rem;
}
/* Dark overlay lives on the tile itself (via the inline linear-gradient over
 * the pattern), so children are transparent. Padding + dividers stay here. */
.orders__tile > * {
  color: #f9fafb;
  padding: 0.75rem 1rem;
  margin: 0;
}
.orders__tile > * + * {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.orders__tile-header h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
}
.orders__tile-order-id,
.orders__tile-date {
  font-size: 0.75rem;
  color: #d1d5db;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
}
.orders__tile-project {
  font-size: 0.8125rem;
  color: #e5e7eb;
}
.orders__tile-project span {
  display: block;
}
.orders__tile-address {
  font-size: 0.75rem;
  color: #d1d5db;
}
.orders__tile-submitted {
  font-size: 0.875rem;
  color: #e5e7eb;
}
.orders__tile-actions {
  margin-top: auto;
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.orders__pager {
  margin-top: 1.5rem;
  justify-content: center;
}
</style>
